import * as core from '@actions/core';
import * as github from '@actions/github';
import * as fs from 'node:fs';
import * as path from 'node:path';
import { enableAutoMerge, renderAutoMergeFailureComment } from './auto-merge';
import { ByteBudget, writeBudgetedUtf8File } from './byte-budget';
import { readConfig, PassKind } from './config';
import { formatDuration, runCodexWithRetries } from './codex';
import { preparePullRequestDiff } from './git-diff';
import { buildReviewPrompt } from './prompts';
import { runPasses } from './pass-execution';
import { prepareReviewInputs, PullRequestSnapshot } from './review-inputs';
import { createRunStorage, prepareOutputDirectory, prepareOwnedReviewDirectory } from './run-storage';
import { ReviewIssue, REVIEW_SCHEMA } from './schema';
import {
  aggregatePasses,
  parseReviewOutput,
  PassResult,
  renderAggregateComment,
  renderPassComment,
} from './results';
import { fencedBlock } from './utils';

type Octokit = ReturnType<typeof github.getOctokit>;

async function run(): Promise<void> {
  const config = readConfig();
  const octokit = github.getOctokit(config.githubToken);
  installRequestRetries(octokit);

  const owner = github.context.repo.owner;
  const repo = github.context.repo.repo;
  const prNumber = resolvePrNumber(config.prNumber);
  const { data: pr } = await octokit.rest.pulls.get({
    owner,
    repo,
    pull_number: prNumber,
  });

  assertPullRequestEligibility({
    prNumber,
    draft: Boolean(pr.draft),
    state: pr.state,
    targetBranch: pr.base.ref,
    defaultBranch: pr.base.repo?.default_branch,
  });
  if (!pr.head.repo || !pr.head.sha || !pr.base.sha || !pr.base.repo?.clone_url || !pr.base.repo.full_name) {
    throw new Error(`Could not resolve PR #${prNumber} head/base metadata.`);
  }

  const [headOwner, headRepoName] = pr.head.repo.full_name.split('/');
  if (!headOwner || !headRepoName) {
    throw new Error(`Could not resolve PR #${prNumber} head repository metadata.`);
  }
  const pullRequest: PullRequestSnapshot = {
    number: prNumber,
    title: pr.title || '',
    author: pr.user?.login || 'unknown',
    body: pr.body || '',
    draft: Boolean(pr.draft),
    state: pr.state,
    base: {
      repo: pr.base.repo.full_name,
      cloneUrl: pr.base.repo.clone_url,
      ref: pr.base.ref,
      sha: pr.base.sha,
    },
    head: {
      repo: pr.head.repo.full_name,
      owner: headOwner,
      repoName: headRepoName,
      ref: pr.head.ref,
      sha: pr.head.sha,
    },
  };
  const headSha = pullRequest.head.sha;
  const baseSha = pullRequest.base.sha;
  core.setOutput('head-sha', headSha);
  core.setOutput('base-sha', baseSha);

  const mergeBaseSha = await resolvePullRequestMergeBase({
    octokit,
    owner,
    repo,
    baseSha,
    headSha,
  });

  const storage = createRunStorage({
    workspace: config.workspace,
    outputDir: config.outputDir,
  });
  const reviewDir = prepareOwnedReviewDirectory({
    workspace: config.workspace,
    reviewDir: config.reviewDir,
    token: storage.token,
    callerOwnedOutputDir: config.outputDir === null ? null : storage.outputDir,
  });
  const reviewBundlePath = path.relative(fs.realpathSync(config.workspace), reviewDir)
    .split(path.sep)
    .join('/');
  const outputDir = prepareOutputDirectory({
    outputDir: storage.outputDir,
    callerOwned: config.outputDir !== null,
    actionOwnedDirectories: [storage.runRoot, reviewDir],
  });
  const metadataBudget = new ByteBudget(config.maxReviewMetadataBytes);
  const schemaFile = path.join(outputDir, 'gate-review-schema.json');
  writeBudgetedUtf8File({
    file: schemaFile,
    content: `${JSON.stringify(REVIEW_SCHEMA)}\n`,
    budget: metadataBudget,
    description: 'Review schema',
  });

  const preparedDiff = preparePullRequestDiff({
    temporaryDirectory: storage.gitDir,
    outputFile: path.join(reviewDir, 'pr.diff'),
    remoteUrl: pullRequest.base.cloneUrl,
    prNumber,
    baseSha,
    headSha,
    mergeBaseSha,
    githubToken: config.githubToken,
    maxDiffBytes: config.maxDiffBytes,
    maxGitRepositoryBytes: config.maxGitRepositoryBytes,
  });
  core.info(`Prepared ${preparedDiff.diffBytes}-byte GitHub merge-base diff for PR #${prNumber} at ${headSha}; temporary Git repository measured ${preparedDiff.gitRepositoryBytes} bytes.`);
  await prepareReviewInputs({
    octokit,
    owner,
    repo,
    workspace: config.workspace,
    reviewDir,
    pullRequest,
    includeRebuttal: config.includeRebuttal,
    maxSnapshotBytes: config.maxSnapshotBytes,
    maxTotalSnapshotBytes: config.maxTotalSnapshotBytes,
    metadataBudget,
  });

  const diagnosticBudget = new ByteBudget(config.maxDiagnosticBytes);
  const runPass = async (pass: PassKind): Promise<PassResult> => {
    const title = passTitle(pass);
    const prompt = buildReviewPrompt({
      workspace: config.workspace,
      reviewDir: reviewBundlePath,
      policyPaths: config.policyPaths,
      promptPath: pass === 'standard' ? config.standardPromptPath : config.adversarialPromptPath,
      pass,
      extraInstructions: config.extraInstructions,
    });
    const promptFile = path.join(outputDir, `${pass}-prompt.md`);
    const outputFile = path.join(outputDir, `${pass}-review.json`);
    writeBudgetedUtf8File({
      file: promptFile,
      content: prompt,
      budget: metadataBudget,
      description: `${title} prompt`,
    });

    core.info(`Running ${title} for PR #${prNumber} at ${headSha}.`);
    const codexResult = await runCodexWithRetries({
      label: title,
      workspace: config.workspace,
      prompt,
      schemaFile,
      outputFile,
      outputDir,
      tempDir: storage.tempDir,
      diagnosticBudget,
      maxReviewOutputBytes: config.maxReviewOutputBytes,
      attemptPrefix: `${pass}-review`,
      model: config.model,
      sandbox: config.sandbox,
      safePath: config.safePath,
      maxAttempts: config.maxAttempts,
      initialBackoffSeconds: config.initialBackoffSeconds,
      info: (message) => core.info(message),
      log: (message) => core.warning(message),
    });

    let result: PassResult;
    if (codexResult.status !== 0) {
      result = {
        key: pass,
        title,
        exitCode: codexResult.status,
        attempt: codexResult.attempt,
        maxAttempts: config.maxAttempts,
        durationMs: codexResult.durationMs,
        eventsFile: codexResult.eventsFile,
        stderrFile: codexResult.stderrFile,
        eventCount: codexResult.eventCount,
        commandCount: codexResult.commandCount,
        usageSummary: codexResult.usageSummary,
        stderrTail: codexResult.stderrTail,
        failureDiagnostic: codexResult.failureDiagnostic,
        diagnosticsTruncated: codexResult.diagnosticsTruncated,
        review: null,
        failure: `Codex exited with code ${codexResult.status}.`,
      };
    } else {
      try {
        result = {
          key: pass,
          title,
          exitCode: codexResult.status,
          attempt: codexResult.attempt,
          maxAttempts: config.maxAttempts,
          durationMs: codexResult.durationMs,
          eventsFile: codexResult.eventsFile,
          stderrFile: codexResult.stderrFile,
          eventCount: codexResult.eventCount,
          commandCount: codexResult.commandCount,
          usageSummary: codexResult.usageSummary,
          stderrTail: codexResult.stderrTail,
          failureDiagnostic: null,
          diagnosticsTruncated: codexResult.diagnosticsTruncated,
          review: parseReviewOutput(outputFile, config.maxReviewOutputBytes),
          failure: null,
        };
      } catch (error) {
        result = {
          key: pass,
          title,
          exitCode: codexResult.status,
          attempt: codexResult.attempt,
          maxAttempts: config.maxAttempts,
          durationMs: codexResult.durationMs,
          eventsFile: codexResult.eventsFile,
          stderrFile: codexResult.stderrFile,
          eventCount: codexResult.eventCount,
          commandCount: codexResult.commandCount,
          usageSummary: codexResult.usageSummary,
          stderrTail: codexResult.stderrTail,
          failureDiagnostic: null,
          diagnosticsTruncated: codexResult.diagnosticsTruncated,
          review: null,
          failure: error instanceof Error ? error.message : String(error),
        };
      }
    }
    core.info(`${title}: ${result.review ? result.review.verdict : 'FAILED'} after ${formatDuration(result.durationMs)}. Blocking issues: ${result.review?.blocking_issues.length || 0}.`);
    return result;
  };

  if (config.parallelPasses && config.passes.length > 1) {
    core.info(`Running ${config.passes.length} Codex Review passes concurrently in read-only sandboxes.`);
  }
  const results = await runPasses({
    passes: config.passes,
    parallel: config.parallelPasses,
    runPass,
    onPassComplete: async (result) => {
      if (!config.postComments) {
        return;
      }
      await postCommentBestEffort(octokit, owner, repo, prNumber, renderPassComment({
        headSha,
        baseSha,
        result,
      }), `${result.title} comment`);
    },
  });

  await assertCurrentPullRequestEligibility({
    octokit,
    owner,
    repo,
    prNumber,
    reviewedHeadSha: headSha,
    operation: 'publishing the aggregate verdict',
  });

  if (config.postComments) {
    await postCommentBestEffort(octokit, owner, repo, prNumber, renderAggregateComment({
      headSha,
      baseSha,
      results,
    }), 'aggregate review comment');
  }

  const failures = results.filter((result) => result.failure !== null || result.review === null);
  const aggregate = aggregatePasses(results);
  core.setOutput('verdict', aggregate.verdict);
  core.setOutput('blocking-count', String(aggregate.blockingCount));
  annotateBlockingIssues(results);
  await writeJobSummary({
    prNumber,
    headSha,
    baseSha,
    reviewDir,
    results,
  });

  if (failures.length > 0) {
    throw new Error(`Codex Review failed closed: ${failures.map((result) => `${result.key}: ${result.failure || 'review did not produce a verdict'}`).join('; ')}`);
  }

  if (aggregate.verdict === 'APPROVE' && config.autoMerge) {
    await assertCurrentPullRequestEligibility({
      octokit,
      owner,
      repo,
      prNumber,
      reviewedHeadSha: headSha,
      operation: 'requesting auto-merge',
    });
    const autoMergeResult = await enableAutoMerge({
      owner,
      repo,
      prNumber,
      reviewedHeadSha: headSha,
      githubToken: config.githubToken,
    });
    if (autoMergeResult.status === 'failed') {
      await postCommentBestEffort(octokit, owner, repo, prNumber, renderAutoMergeFailureComment({
        result: autoMergeResult,
        reviewedHeadSha: headSha,
      }), 'auto-merge failure comment');
    }
  }

  if (aggregate.verdict === 'BLOCK' && config.failOnBlock) {
    core.setFailed(`Codex Review BLOCK: ${aggregate.blockingCount} pass finding(s). See the job summary for pass details and diagnostics paths.`);
  }

  core.info(`Codex Review aggregate verdict: ${aggregate.verdict}. Review inputs: ${reviewDir}.`);
}

function assertPullRequestEligibility({
  prNumber,
  draft,
  state,
  targetBranch,
  defaultBranch,
}: {
  prNumber: number;
  draft: boolean;
  state: string;
  targetBranch: string;
  defaultBranch: string | undefined;
}): void {
  if (draft) {
    throw new Error(`Codex Review requires a ready-for-review PR; PR #${prNumber} is a draft.`);
  }
  if (!defaultBranch) {
    throw new Error(`Could not resolve the repository default branch for PR #${prNumber}.`);
  }
  if (targetBranch !== defaultBranch) {
    throw new Error(
      `Codex Review requires PRs to target the repository default branch `
      + `${JSON.stringify(defaultBranch)}; PR #${prNumber} targets ${JSON.stringify(targetBranch)}.`,
    );
  }
  if (state !== 'open') {
    throw new Error(`Codex Review requires an open PR; current state is ${state}.`);
  }
}

async function assertCurrentPullRequestEligibility({
  octokit,
  owner,
  repo,
  prNumber,
  reviewedHeadSha,
  operation,
}: {
  octokit: Octokit;
  owner: string;
  repo: string;
  prNumber: number;
  reviewedHeadSha: string;
  operation: string;
}): Promise<void> {
  const { data: current } = await octokit.rest.pulls.get({
    owner,
    repo,
    pull_number: prNumber,
  });
  assertPullRequestEligibility({
    prNumber,
    draft: Boolean(current.draft),
    state: current.state,
    targetBranch: current.base.ref,
    defaultBranch: current.base.repo?.default_branch,
  });
  if (current.head.sha !== reviewedHeadSha) {
    throw new Error(
      `Pull request #${prNumber} head changed before ${operation}: `
      + `reviewed ${reviewedHeadSha}, current ${current.head.sha}.`,
    );
  }
}

async function resolvePullRequestMergeBase({
  octokit,
  owner,
  repo,
  baseSha,
  headSha,
}: {
  octokit: Octokit;
  owner: string;
  repo: string;
  baseSha: string;
  headSha: string;
}): Promise<string> {
  const { data: comparison } = await octokit.rest.repos.compareCommitsWithBasehead({
    owner,
    repo,
    basehead: `${baseSha}...${headSha}`,
    page: 1,
    per_page: 1,
  });
  if (comparison.base_commit.sha !== baseSha) {
    throw new Error(`GitHub compare base mismatch: expected ${baseSha}, received ${comparison.base_commit.sha}.`);
  }
  const mergeBaseSha = comparison.merge_base_commit.sha;
  if (!/^[0-9a-f]{40}$/i.test(mergeBaseSha)) {
    throw new Error(`GitHub did not return a valid pull request merge base: ${mergeBaseSha}.`);
  }
  return mergeBaseSha;
}

function resolvePrNumber(configPrNumber: number | null): number {
  if (configPrNumber !== null) {
    return configPrNumber;
  }
  const payloadPr = github.context.payload.pull_request;
  const value = payloadPr && typeof payloadPr === 'object' && 'number' in payloadPr
    ? Number(payloadPr.number)
    : 0;
  if (!Number.isInteger(value) || value < 1) {
    throw new Error('Codex Review requires pull request context or pr-number input.');
  }
  return value;
}

function passTitle(pass: PassKind): string {
  return pass === 'adversarial' ? 'Adversarial Codex Review' : 'Standard Codex Review';
}

async function writeJobSummary({
  prNumber,
  headSha,
  baseSha,
  reviewDir,
  results,
}: {
  prNumber: number;
  headSha: string;
  baseSha: string;
  reviewDir: string;
  results: PassResult[];
}): Promise<void> {
  const aggregate = aggregatePasses(results);
  const failures = results.filter((result) => result.failure !== null || result.review === null);
  const status = failures.length > 0 ? 'FAILED' : aggregate.verdict;
  const lines = [
    '# Codex Review',
    '',
    `PR: #${prNumber}`,
    `Head: \`${headSha}\``,
    `Base: \`${baseSha}\``,
    `Status: **${status}**`,
    `Pass findings: **${aggregate.blockingCount}**`,
    '',
    '## Passes',
    '',
    '| Pass | Result | Attempt | Runtime | Events | Commands | Usage | Diagnostics |',
    '| --- | --- | ---: | ---: | ---: | ---: | --- | --- |',
    ...results.map((result) => [
      escapeTable(result.title),
      result.review ? result.review.verdict : 'FAILED',
      `${result.attempt}/${result.maxAttempts}`,
      formatDuration(result.durationMs),
      String(result.eventCount),
      String(result.commandCount),
      escapeTable(result.usageSummary || '-'),
      escapeTable(`${result.eventsFile}${result.diagnosticsTruncated ? ' (truncated)' : ''}${result.failure ? `; ${result.failure}` : ''}`),
    ].join(' | ')).map((row) => `| ${row} |`),
    '',
    `Review inputs: \`${reviewDir}\``,
  ];

  const findings = blockingFindings(results);
  if (findings.length > 0) {
    lines.push(
      '',
      '## Blocking Findings',
      '',
      '| Pass | Location | Description |',
      '| --- | --- | --- |',
      ...findings.slice(0, 20).map(({ result, issue }) => `| ${escapeTable(result.title)} | ${escapeTable(`${issue.file}:${issue.line}`)} | ${escapeTable(truncateText(issue.description, 500))} |`),
    );
    if (findings.length > 20) {
      lines.push('', `_Showing 20 of ${findings.length} blocking findings._`);
    }
  }

  if (failures.length > 0) {
    lines.push('', '## Failure Details');
    for (const result of failures) {
      lines.push('', `### ${result.title}`, '', result.failure || 'Review failed.');
      if (result.failureDiagnostic) {
        lines.push('', 'Reviewer diagnostic:', '', fencedBlock('text', result.failureDiagnostic));
      }
    }
  }

  await core.summary.addRaw(lines.join('\n'), true).write();
}

function annotateBlockingIssues(results: PassResult[]): void {
  const seen = new Set<string>();
  let emitted = 0;
  for (const { result, issue } of blockingFindings(results)) {
    const key = `${issue.file}:${issue.line}:${issue.description}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    core.error(issue.description, {
      title: `${result.title}: blocker`,
      file: issue.file,
      startLine: issue.line,
    });
    emitted += 1;
    if (emitted >= 10) {
      break;
    }
  }
}

function blockingFindings(results: PassResult[]): Array<{ result: PassResult; issue: ReviewIssue }> {
  return results.flatMap((result) => (result.review?.blocking_issues || []).map((issue) => ({ result, issue })));
}

function escapeTable(value: string): string {
  return value.replace(/\|/g, '\\|').replace(/\r?\n/g, '<br>');
}

function truncateText(value: string, maxLength: number): string {
  return value.length > maxLength ? `${value.slice(0, maxLength - 3)}...` : value;
}

async function postCommentBestEffort(
  octokit: Octokit,
  owner: string,
  repo: string,
  issueNumber: number,
  body: string,
  description: string,
): Promise<void> {
  try {
    await octokit.rest.issues.createComment({
      owner,
      repo,
      issue_number: issueNumber,
      body,
    });
  } catch (error) {
    core.warning(`Could not post ${description}; Codex Review will continue: ${error instanceof Error ? error.message : String(error)}`);
  }
}

function installRequestRetries(octokit: Octokit): void {
  octokit.hook.wrap('request', async (
    request: (options: unknown) => Promise<unknown>,
    options: unknown,
  ) => {
    const maxRetries = 3;
    let lastError: unknown;
    for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
      try {
        return await request(options);
      } catch (error) {
        lastError = error;
        const status = typeof error === 'object' && error && 'status' in error
          ? Number((error as { status?: number }).status)
          : 0;
        if ((status >= 500 || status === 429) && attempt < maxRetries) {
          const delay = (2 ** attempt) * 1000 + Math.floor(Math.random() * 500);
          core.warning(`GitHub API error (status ${status}), retrying in ${delay}ms (attempt ${attempt + 2}/${maxRetries + 1})`);
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }
        throw error;
      }
    }
    throw lastError;
  });
}

run().catch((error) => {
  core.setFailed(error instanceof Error ? error.message : String(error));
});
